/**
 * TutorialOverlay Component
 * Provides an interactive step-by-step onboarding walkthrough overlay
 * highlighting key UI elements across different routes (Trust, About, Contact, Login).
 * Supports spotlight positioning, progress tracking, and step navigation.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './TutorialOverlay.css';

// Storage keys for tracking tour status (pending, completed, skipped) per route
const LOGIN_TOUR_PENDING_KEY = 'clipstone_login_tour_pending';
const LOGIN_TOUR_COMPLETED_KEY = 'clipstone_login_tour_completed';
const LOGIN_TOUR_SKIPPED_KEY = 'clipstone_login_tour_skipped';
const TRUST_TOUR_PENDING_KEY = 'clipstone_trust_tour_pending';
const TRUST_TOUR_COMPLETED_KEY = 'clipstone_trust_tour_completed';
const TRUST_TOUR_SKIPPED_KEY = 'clipstone_trust_tour_skipped';
const ABOUT_TOUR_PENDING_KEY = 'clipstone_about_tour_pending';
const ABOUT_TOUR_COMPLETED_KEY = 'clipstone_about_tour_completed';
const ABOUT_TOUR_SKIPPED_KEY = 'clipstone_about_tour_skipped';
const CONTACT_TOUR_PENDING_KEY = 'clipstone_contact_tour_pending';
const CONTACT_TOUR_COMPLETED_KEY = 'clipstone_contact_tour_completed';
const CONTACT_TOUR_SKIPPED_KEY = 'clipstone_contact_tour_skipped';
const ACTIVE_TOUR_KEY = 'clipstone_active_tour';
const ACTIVE_TOUR_STEP_KEY = 'clipstone_active_tour_step';

const createSteps = (pathname, activeTour) => {

  const trustSteps = [
    {
      selector: '.hero-value-prop h1',
      title: 'Trust homepage overview',
      description: 'This is the main entry point for trust information, activities, and bookings.',
    },
    {
      selector: '.trust-page .hero-image-wrapper',
      title: 'Trust gallery spotlight',
      description: 'This square slideshow highlights the Trust visually and rotates through featured images.',
    },
    {
      selector: '.trust-page .hero-main-dots',
      title: 'Slideshow controls',
      description: 'Use these dots to manually switch between trust homepage slideshow images.',
    },
    {
      selector: '#trust-history-button',
      title: 'History shortcut',
      description: 'Use this button to open the Trust history timeline page.',
      clickable: false,
    },
    {
      selector: '#trust-view-locations-button',
      title: 'Location shortcut',
      description: 'Use this button when you want to view institute location details.',
      clickable: false,
    },
    {
      selector: '#trust-contact-button',
      title: 'Trust contact shortcut',
      description: 'Use this button to jump directly to the Trust contact section.',
      clickable: false,
    },
    {
      selector: '#bookings',
      title: 'Events and bookings',
      description: 'Here you can browse upcoming sessions and book places for activities.',
    },
    {
      selector: '.card-grid',
      title: 'Category quick access',
      description: 'These cards link directly to trust categories so visitors can jump to details quickly.',
    },
    {
      selector: '#trust-donate-section',
      title: 'Donation section',
      description: 'Use this section to support welfare initiatives and community projects.',
    },
  ];

  const accountSteps = [
    {
      selector: '#nav-logo',
      title: 'Quick home access',
      description: 'This logo always returns you to the public homepage.',
    },
    {
      selector: '#account-avatar-section',
      title: 'Your account profile',
      description: 'Your member details are shown here, including your email and username.',
    },
    {
      selector: '#account-back-home',
      title: 'Return to home',
      description: 'Click this button to go back to the homepage and continue browsing the site.',
    },
    {
      selector: '#account-sign-out',
      title: 'Sign out safely',
      description: 'Use this button when you have finished your session.',
    },
  ];

  const homeSteps = [
    {
      selector: '#nav-logo',
      title: 'Start here',
      description: 'Use the site logo to jump back to the home page at any time.',
    },
    {
      selector: '#main-content',
      title: 'Explore the site',
      description: 'Browse the main content area to discover upcoming events, activities and stories.',
    },
  ];

  const loginSteps = [
    {
      selector: '#nav-logo',
      title: 'Home button',
      description: 'Use the logo to return to the homepage at any time.',
    },
    {
      selectors: ['#nav-login-mobile', '#nav-login', '.nav-mobile-auth-btn'],
      title: 'Login / Sign Up button',
      description: 'This is the entry point for guest login and registration.',
    },
    {
      selector: '#login-form',
      title: 'Login form',
      description: 'Enter your email and password here, or switch to sign up if you are new.',
    },
  ];

  const aboutSteps = [
    {
      selector: '#about-page-heading',
      title: 'About Clipstone Welfare',
      description: 'This section introduces the Trust mission and long-term community focus.',
    },
    {
      selector: '#about-info-grid',
      title: 'Core information panels',
      description: 'Review goals, achievements, purpose, and community values in these cards.',
    },
    {
      selector: '#about-meet-trustees-button',
      title: 'Continue to trustees',
      description: 'Use this button to open the Meet Our Trustees page.',
    },
  ];

  const contactSteps = [
    {
      selector: '#contact-page-heading',
      title: 'Contact Us page',
      description: 'This page shows how to contact the Trust for enquiries and support.',
    },
    {
      selector: '#contact-email-link',
      title: 'Send an email',
      description: 'Click here to open your email app and send your enquiry directly.',
    },
    {
      selector: '#contact-hours-section',
      title: 'Opening hours',
      description: 'Check today’s opening hours before visiting or calling the Trust.',
    },
  ];

  const trusteesSteps = [
    {
      selector: '.trustees-title',
      title: 'Meet Our Trustees',
      description: 'This heading confirms you are in the trustee directory.',
    },
    {
      selector: '.profile-card',
      title: 'Explore trustee profiles',
      description: 'Hover or click a card to view contact details and activity links.',
    },
    {
      selector: '.profile-card-click-hint',
      selectors: ['.profile-card-click-hint', '.profile-card-overlay-activities', '.profile-card'],
      title: 'Open linked activity pages',
      description: 'When shown, this hint means clicking the card opens the trustee’s associated activity.',
    },
  ];

  const communitySteps = [
    {
      selector: '.community-page h1',
      title: 'Community overview',
      description: 'Discover the community services and activities supported by the Welfare.',
    },
    {
      selector: '.community-page footer',
      title: 'Get involved',
      description: 'Use these contact details to reach out and join in.',
    },
  ];

  const historySteps = [
    {
      selector: '.history-header h1',
      title: 'Read the history',
      description: 'Follow the timeline to see how the Welfare grew over time.',
    },
    {
      selector: '.timeline-item',
      title: 'Explore milestones',
      description: 'Each timeline entry highlights an important moment in the Trust’s story.',
    },
  ];

  const socialSteps = [
    {
      selector: '#social-hero-main',
      title: 'Social homepage overview',
      description: 'This hero introduces the social club and links quickly to booking, contact, and staff pages.',
    },
    {
      selector: '#social-temp-offers',
      title: 'Things we offer slideshow',
      description: 'Browse social offerings and use the slide dots to move between highlight cards.',
    },
    {
      selector: '#social-experiences',
      title: 'Experience categories',
      description: 'These cards explain the types of social events and community experiences available.',
    },
    {
      selector: '#social-rhythm',
      title: 'Weekly social rhythm',
      description: 'This timeline shows a typical week and helps visitors understand regular club activity.',
    },
    {
      selector: '#social-quick-links',
      title: 'Quick links',
      description: 'Use these links to jump directly to booking, staff, and contact pages.',
    },
  ];

  const socialStaffSteps = [
    {
      selector: '#social-staff-hero',
      title: 'Social staff page',
      description: 'This page introduces the social club team who handle enquiries and event planning.',
    },
    {
      selector: '#social-staff-grid',
      title: 'Staff cards',
      description: 'Each card shows a team member\'s name, role, phone, and email contact details.',
    },
  ];

  const socialBookingSteps = [
    {
      selector: '#social-booking-hero',
      title: 'Booking and pricing overview',
      description: 'This page lets you view room capacities, pricing, and submit a room hire request.',
    },
    {
      selector: '#social-booking-pricing-grid',
      title: 'Suite pricing',
      description: 'Compare the Portland Suite and Kerry Suite costs before submitting a booking.',
    },
    {
      selector: '#social-booking-form-shell',
      title: 'Room hire form',
      description: 'Fill in your details, choose a suite, preferred date, and submit your hire request here.',
    },
    {
      selector: '.social-rental-total-box',
      title: 'Estimated cost summary',
      description: 'This panel updates live based on attendee count, catering, and DJ options.',
    },
  ];

  const socialContactSteps = [
    {
      selector: '#social-contact-hero',
      title: 'Social club contact page',
      description: 'Here you can find opening times and direct contact details for the social club.',
    },
    {
      selector: '#social-contact-grid',
      title: 'Contact details',
      description: 'Club and office opening times, phone, and email are all listed in these cards.',
    },
    {
      selector: '.social-contact-link',
      title: 'Direct contact links',
      description: 'Use these phone and email links to contact the social team immediately.',
    },
  ];

  const privacySteps = [
    {
      selector: '#privacy-page-heading',
      title: 'Privacy Policy overview',
      description: 'This page explains how information is collected, used and protected.',
    },
    {
      selector: '#privacy-rights-section',
      title: 'Your rights',
      description: 'This section explains how to request updates, access, or removal of personal data.',
    },
  ];

  const termsSteps = [
    {
      selector: '#terms-page-heading',
      title: 'Terms of Service overview',
      description: 'This page outlines acceptable use and key website terms.',
    },
    {
      selector: '#terms-rules-section',
      title: 'Website rules',
      description: 'Use this section to understand operational and conduct expectations.',
    },
  ];

  const authConfirmSteps = [
    {
      selector: '#auth-confirm-title',
      title: 'Email confirmation status',
      description: 'This page verifies your account and guides you back to login when complete.',
    },
  ];

  const trustCategorySteps = [
    {
      selector: '#trust-category-overview-heading',
      title: 'Category overview',
      description: 'This heading confirms which category you are currently viewing.',
    },
    {
      selector: '#trust-category-upcoming-event-panel',
      title: 'Upcoming event panel',
      description: 'This section shows the next event for this category with date and location details.',
    },
    {
      selector: '#trust-category-book-event-target',
      title: 'Book this specific event',
      description: 'Use this area to book the specific upcoming event when it is available.',
    },
    {
      selector: '#trust-category-activity-info-panel',
      title: 'Activity information',
      description: 'This section lists category activities, linked trustees, images, and any external activity website links.',
    },
  ];

  if (activeTour === 'home') return homeSteps;
  if (activeTour === 'trust') return trustSteps;
  if (activeTour === 'about') return aboutSteps;
  if (activeTour === 'login') return loginSteps;
  if (activeTour === 'account') return accountSteps;
  if (activeTour === 'contact') return contactSteps;
  if (activeTour === 'trustees') return trusteesSteps;
  if (activeTour === 'community') return communitySteps;
  if (activeTour === 'history') return historySteps;
  if (activeTour === 'social') return socialSteps;
  if (activeTour === 'social-staff') return socialStaffSteps;
  if (activeTour === 'social-booking') return socialBookingSteps;
  if (activeTour === 'social-contact') return socialContactSteps;
  if (activeTour === 'privacy') return privacySteps;
  if (activeTour === 'terms') return termsSteps;
  if (activeTour === 'auth-confirm') return authConfirmSteps;
  if (activeTour === 'trust-category') return trustCategorySteps;

  if (pathname === '/') return homeSteps;
  if (pathname === '/trust') return trustSteps;
  if (pathname === '/login') return loginSteps;
  if (pathname === '/about') return aboutSteps;
  if (pathname === '/contact') return contactSteps;
  if (pathname === '/trustees') return trusteesSteps;
  if (pathname === '/community') return communitySteps;
  if (pathname === '/history') return historySteps;
  if (pathname === '/social') return socialSteps;
  if (pathname === '/social/staff') return socialStaffSteps;
  if (pathname === '/social/booking') return socialBookingSteps;
  if (pathname === '/social/contact') return socialContactSteps;
  if (pathname === '/privacy') return privacySteps;
  if (pathname === '/terms') return termsSteps;
  if (pathname === '/auth-confirm') return authConfirmSteps;
  if (pathname.startsWith('/trust/')) return trustCategorySteps;
  if (pathname.startsWith('/account')) return accountSteps;
  return [];
};

const getActiveTour = () => window.localStorage.getItem(ACTIVE_TOUR_KEY);
const setActiveTour = (tour) => window.localStorage.setItem(ACTIVE_TOUR_KEY, tour);
const clearActiveTour = () => {
  window.localStorage.removeItem(ACTIVE_TOUR_KEY);
  window.localStorage.removeItem(ACTIVE_TOUR_STEP_KEY);
  window.dispatchEvent(new Event('tutorial-change'));
};
const getActiveTourStep = () => {
  if (!window.localStorage.getItem(ACTIVE_TOUR_KEY)) return 0;
  const stored = window.localStorage.getItem(ACTIVE_TOUR_STEP_KEY);
  return Number(stored) || 0;
};
const setActiveTourStep = (step) => {
  window.localStorage.setItem(ACTIVE_TOUR_STEP_KEY, String(step));
};

const getTourAllowedPaths = (tour) => {
  if (tour === 'home') return ['/'];
  if (tour === 'login') return ['/login'];
  if (tour === 'trust') return ['/trust'];
  if (tour === 'about') return ['/about'];
  if (tour === 'contact') return ['/contact'];
  if (tour === 'trustees') return ['/trustees'];
  if (tour === 'community') return ['/community'];
  if (tour === 'history') return ['/history'];
  if (tour === 'social') return ['/social'];
  if (tour === 'social-staff') return ['/social/staff'];
  if (tour === 'social-booking') return ['/social/booking'];
  if (tour === 'social-contact') return ['/social/contact'];
  if (tour === 'privacy') return ['/privacy'];
  if (tour === 'terms') return ['/terms'];
  if (tour === 'auth-confirm') return ['/auth-confirm'];
  if (tour === 'trust-category') return ['/trust/'];
  if (tour === 'account') return ['/account'];
  return [];
};

const pathMatchesTour = (tour, pathname) => {
  if (!tour || !pathname) return false;
  if (tour === 'trust-category') return pathname.startsWith('/trust/');
  if (tour === 'account') return pathname.startsWith('/account');
  return getTourAllowedPaths(tour).includes(pathname);
};

const resolveStepSelectors = (step) => {
  if (!step) return [];
  if (Array.isArray(step.selectors) && step.selectors.length > 0) {
    return step.selectors.filter(Boolean);
  }
  if (typeof step.selector === 'string' && step.selector.trim()) {
    return [step.selector.trim()];
  }
  return [];
};

export const getTourForPath = (pathname) => {
  if (pathname.startsWith('/admin') || pathname === '/admin-account') return null;
  if (pathname === '/') return 'home';
  if (pathname === '/login') return 'login';
  if (pathname === '/trust') return 'trust';
  if (pathname === '/about') return 'about';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/trustees') return 'trustees';
  if (pathname === '/community') return 'community';
  if (pathname === '/history') return 'history';
  if (pathname === '/social') return 'social';
  if (pathname === '/social/staff') return 'social-staff';
  if (pathname === '/social/booking') return 'social-booking';
  if (pathname === '/social/contact') return 'social-contact';
  if (pathname === '/privacy') return 'privacy';
  if (pathname === '/terms') return 'terms';
  if (pathname === '/auth-confirm') return 'auth-confirm';
  if (pathname.startsWith('/trust/')) return 'trust-category';
  if (pathname.startsWith('/account')) return 'account';
  return null;
};

export const startTourForPath = (pathname) => {
  if (typeof window === 'undefined') return false;
  const tour = getTourForPath(pathname);
  if (!tour) return false;

  // Manual help launches should always replay the selected tutorial.
  window.localStorage.removeItem(getTourStorageKey(tour, 'completed'));
  window.localStorage.removeItem(getTourStorageKey(tour, 'skipped'));
  window.localStorage.setItem(getTourStorageKey(tour, 'pending'), 'true');

  setActiveTour(tour);
  setActiveTourStep(0);
  window.dispatchEvent(new Event('tutorial-change'));
  return true;
};

const getCardStyleForTarget = (rect) => {
  const style = {
    position: 'fixed',
    zIndex: 10001,
    width: 'min(420px, calc(100% - 40px))',
  };

  const cardWidth = Math.min(420, window.innerWidth - 40);
  const cardHeight = 240;
  const margin = 16;
  const availableRight = window.innerWidth - rect.right - margin;
  const availableLeft = rect.left - margin;
  const availableTop = rect.top - margin;
  const availableBottom = window.innerHeight - rect.bottom - margin;

  if (availableRight >= cardWidth) {
    style.left = `${rect.right + margin}px`;
    style.top = `${Math.min(Math.max(margin, rect.top), window.innerHeight - cardHeight - margin)}px`;
  } else if (availableLeft >= cardWidth) {
    style.left = `${Math.max(margin, rect.left - cardWidth - margin)}px`;
    style.top = `${Math.min(Math.max(margin, rect.top), window.innerHeight - cardHeight - margin)}px`;
  } else if (availableTop >= cardHeight) {
    style.top = `${rect.top - cardHeight - margin}px`;
    style.left = `${Math.min(Math.max(margin, rect.left), window.innerWidth - cardWidth - margin)}px`;
  } else if (availableBottom >= cardHeight) {
    style.top = `${rect.bottom + margin}px`;
    style.left = `${Math.min(Math.max(margin, rect.left), window.innerWidth - cardWidth - margin)}px`;
  } else {
    style.top = `${margin}px`;
    style.left = `${margin}px`;
  }

  return style;
};

const isTourAllowed = (pathname) => {
  if (typeof window === 'undefined') return false;

  const activeTour = getActiveTour();
  if (activeTour) {
    const allowedByPath = pathMatchesTour(activeTour, pathname);
    if (!allowedByPath) {
      return false;
    }
    const completed = window.localStorage.getItem(`clipstone_${activeTour}_tour_completed`) === 'true';
    const skipped = window.localStorage.getItem(`clipstone_${activeTour}_tour_skipped`) === 'true';
    return !completed && !skipped;
  }

  if (pathname === '/login') {
    const pending = window.localStorage.getItem(LOGIN_TOUR_PENDING_KEY) === 'true';
    const completed = window.localStorage.getItem(LOGIN_TOUR_COMPLETED_KEY) === 'true';
    const skipped = window.localStorage.getItem(LOGIN_TOUR_SKIPPED_KEY) === 'true';
    return pending && !completed && !skipped;
  }

  if (pathname === '/trust') {
    const pending = window.localStorage.getItem(TRUST_TOUR_PENDING_KEY) === 'true';
    const completed = window.localStorage.getItem(TRUST_TOUR_COMPLETED_KEY) === 'true';
    const skipped = window.localStorage.getItem(TRUST_TOUR_SKIPPED_KEY) === 'true';
    return pending && !completed && !skipped;
  }

  if (pathname === '/about') {
    const pending = window.localStorage.getItem(ABOUT_TOUR_PENDING_KEY) === 'true';
    const completed = window.localStorage.getItem(ABOUT_TOUR_COMPLETED_KEY) === 'true';
    const skipped = window.localStorage.getItem(ABOUT_TOUR_SKIPPED_KEY) === 'true';
    return pending && !completed && !skipped;
  }

  return false;
};

const getTourStorageKey = (tour, suffix) => `clipstone_${tour}_tour_${suffix}`;

const markTourCompleted = (pathname) => {
  const activeTour = getActiveTour();
  if (activeTour) {
    window.localStorage.setItem(getTourStorageKey(activeTour, 'completed'), 'true');
    window.localStorage.removeItem(getTourStorageKey(activeTour, 'pending'));
    clearActiveTour();
    return;
  }

  if (pathname === '/login') {
    window.localStorage.setItem(LOGIN_TOUR_COMPLETED_KEY, 'true');
    window.localStorage.removeItem(LOGIN_TOUR_PENDING_KEY);
  } else if (pathname === '/trust') {
    window.localStorage.setItem(TRUST_TOUR_COMPLETED_KEY, 'true');
    window.localStorage.removeItem(TRUST_TOUR_PENDING_KEY);
  } else if (pathname === '/about') {
    window.localStorage.setItem(ABOUT_TOUR_COMPLETED_KEY, 'true');
    window.localStorage.removeItem(ABOUT_TOUR_PENDING_KEY);
  } else if (pathname === '/contact') {
    window.localStorage.setItem(CONTACT_TOUR_COMPLETED_KEY, 'true');
    window.localStorage.removeItem(CONTACT_TOUR_PENDING_KEY);
  }
  clearActiveTour();
};

const markTourSkipped = (pathname) => {
  const activeTour = getActiveTour();
  if (activeTour) {
    window.localStorage.setItem(getTourStorageKey(activeTour, 'skipped'), 'true');
    window.localStorage.removeItem(getTourStorageKey(activeTour, 'pending'));
    clearActiveTour();
    return;
  }

  if (pathname === '/login') {
    window.localStorage.setItem(LOGIN_TOUR_SKIPPED_KEY, 'true');
    window.localStorage.removeItem(LOGIN_TOUR_PENDING_KEY);
  } else if (pathname === '/trust') {
    window.localStorage.setItem(TRUST_TOUR_SKIPPED_KEY, 'true');
    window.localStorage.removeItem(TRUST_TOUR_PENDING_KEY);
  } else if (pathname === '/about') {
    window.localStorage.setItem(ABOUT_TOUR_SKIPPED_KEY, 'true');
    window.localStorage.removeItem(ABOUT_TOUR_PENDING_KEY);
  } else if (pathname === '/contact') {
    window.localStorage.setItem(CONTACT_TOUR_SKIPPED_KEY, 'true');
    window.localStorage.removeItem(CONTACT_TOUR_PENDING_KEY);
  }
};

const TutorialOverlay = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [waitingForModalClose, setWaitingForModalClose] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const [cardStyle, setCardStyle] = useState({});
  const prevPathRef = useRef(location.pathname);

  const isElementHighlightable = useCallback((element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    const computed = window.getComputedStyle(element);
    return computed.display !== 'none' && computed.visibility !== 'hidden';
  }, []);

  const getStepTargetElement = useCallback((step) => {
    const selectors = resolveStepSelectors(step);
    if (selectors.length === 0) return null;

    let fallbackElement = null;
    for (const selector of selectors) {
      try {
        const candidate = document.querySelector(selector);
        if (!candidate) continue;
        if (!fallbackElement) fallbackElement = candidate;
        if (isElementHighlightable(candidate)) {
          return candidate;
        }
      } catch (error) {
        console.error('TutorialOverlay: invalid selector', selector, error);
      }
    }

    return fallbackElement;
  }, [isElementHighlightable]);

  const advanceStep = useCallback(() => {
    const nextStep = currentStep + 1;
    if (nextStep >= steps.length) {
      markTourCompleted(location.pathname);
      setVisible(false);
      setWaitingForModalClose(false);
      return;
    }

    setCurrentStep(nextStep);
    setActiveTourStep(nextStep);
    setWaitingForModalClose(false);
  }, [currentStep, location.pathname, steps.length]);

  useEffect(() => {
    const updateSteps = () => {
      const activeTour = getActiveTour();
      const routeSteps = createSteps(location.pathname, activeTour);
      setSteps(routeSteps);

      if (!activeTour) {
        setCurrentStep(0);
        setActiveTourStep(0);
        setWaitingForModalClose(false);
        prevPathRef.current = location.pathname;
        return;
      }

      if (prevPathRef.current !== location.pathname) {
        setCurrentStep(0);
        setActiveTourStep(0);
        setWaitingForModalClose(false);
        prevPathRef.current = location.pathname;
        return;
      }

      const storedStep = getActiveTourStep();
      const normalizedStep = Math.min(Math.max(0, storedStep), Math.max(0, routeSteps.length - 1));
      setCurrentStep(normalizedStep);
      setActiveTourStep(normalizedStep);
    };

    updateSteps();
    window.addEventListener('tutorial-change', updateSteps);
    return () => window.removeEventListener('tutorial-change', updateSteps);
  }, [location.pathname]);

  useEffect(() => {
    const activeTour = getActiveTour();
    if (activeTour) {
      if (!pathMatchesTour(activeTour, location.pathname)) {
        clearActiveTour();
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const normalizedStep = Math.min(currentStep, Math.max(0, steps.length - 1));
    setCurrentStep(normalizedStep);
    const updateVisibility = () => {
      const step = steps[normalizedStep];
      const selectors = resolveStepSelectors(step);

      if (!step || selectors.length === 0) {
        setVisible(false);
        return;
      }

      const elementExists = Boolean(getStepTargetElement(step));
      const modalOpen = step.continueOnClose && Boolean(document.querySelector('.location-modal-overlay'));
      setVisible(isTourAllowed(location.pathname) && steps.length > 0 && elementExists && !modalOpen && !waitingForModalClose);
    };

    updateVisibility();

    const onResize = () => updateVisibility();
    const mutationObserver = new MutationObserver(() => {
      window.requestAnimationFrame(updateVisibility);
    });

    window.addEventListener('resize', onResize);
    if (document.body) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      mutationObserver.disconnect();
    };
  }, [steps, location.pathname, currentStep, waitingForModalClose, getStepTargetElement]);

  useEffect(() => {
    const handleStorageChange = () => {
      const normalizedStep = Math.min(currentStep, Math.max(0, steps.length - 1));
      const step = steps[normalizedStep];
      const elementExists = step ? Boolean(getStepTargetElement(step)) : false;
      const modalOpen = step?.continueOnClose && Boolean(document.querySelector('.location-modal-overlay'));
      setVisible(isTourAllowed(location.pathname) && steps.length > 0 && elementExists && !modalOpen && !waitingForModalClose);
    };

    handleStorageChange();
    window.addEventListener('tutorial-change', handleStorageChange);

    return () => {
      window.removeEventListener('tutorial-change', handleStorageChange);
    };
  }, [location.pathname, steps, currentStep, waitingForModalClose, getStepTargetElement]);

  useEffect(() => {
    if (!visible || steps.length === 0) return;

    const step = steps[currentStep];
    if (!step) return;

    const updateTargetRect = (shouldScroll = false) => {
      if (!step) {
        setTargetRect(null);
        return;
      }

      const element = getStepTargetElement(step);
      if (!element) {
        setTargetRect(null);
        return;
      }

      if (shouldScroll) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }

      const rect = element.getBoundingClientRect();
      const newRect = {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: Math.max(rect.width, 120),
        height: Math.max(rect.height, 60),
      };
      setTargetRect(newRect);
      setCardStyle(getCardStyleForTarget(newRect));
    };

    const scheduleUpdateTargetRect = () => {
      window.requestAnimationFrame(() => updateTargetRect(false));
    };

    updateTargetRect(true);
    window.setTimeout(() => updateTargetRect(false), 260);
    window.addEventListener('resize', scheduleUpdateTargetRect);
    window.addEventListener('scroll', scheduleUpdateTargetRect, true);

    const mutationObserver = new MutationObserver(scheduleUpdateTargetRect);
    const resizeObserver = new ResizeObserver(scheduleUpdateTargetRect);

    if (document.body) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    const currentTarget = getStepTargetElement(step);
    if (currentTarget) {
      resizeObserver.observe(currentTarget);
    }
    resizeObserver.observe(document.documentElement);

    const handleTutorialCommand = (event) => {
      if (event?.detail?.action === 'advance') {
        advanceStep();
      }
    };
    window.addEventListener('tutorial-command', handleTutorialCommand);

    return () => {
      window.removeEventListener('resize', scheduleUpdateTargetRect);
      window.removeEventListener('scroll', scheduleUpdateTargetRect, true);
      window.removeEventListener('tutorial-command', handleTutorialCommand);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [visible, currentStep, steps, advanceStep, getStepTargetElement]);

  const handleSkip = () => {
    markTourSkipped(location.pathname);
    setVisible(false);
    setWaitingForModalClose(false);
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!targetRect) return;

    const x = event.clientX;
    const y = event.clientY;
    const inside =
      x >= targetRect.left &&
      x <= targetRect.left + targetRect.width &&
      y >= targetRect.top &&
      y <= targetRect.top + targetRect.height;

    if (!inside) return;

    const step = steps[currentStep];
    if (step.clickable === false) {
      advanceStep();
      return;
    }

    const element = getStepTargetElement(step);
    if (element && typeof element.click === 'function') {
      element.click();
    }

    if (step.continueOnClose) {
      setWaitingForModalClose(true);
      return;
    }

    // Delay so the browser can execute any real navigation if it occurs.
    window.setTimeout(advanceStep, 120);
  };

  if (!visible) return null;

  const step = steps[currentStep] || {};

  return (
    <div className="tutorial-backdrop">
      <div className="tutorial-card" style={cardStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div className="tutorial-header">
          <h3>{step.title || 'Website tour'}</h3>
          <button className="tutorial-skip" onClick={handleSkip} type="button">
            Skip Tutorial
          </button>
        </div>
        <p>{step.description || 'Follow the highlighted area and click it to continue.'}</p>
        <div className="tutorial-footer">
          <span>Step {currentStep + 1} of {steps.length}</span>
          <button className="tutorial-next" onClick={advanceStep} type="button">
            Continue
          </button>
        </div>
      </div>

      {targetRect && (
        <div
          className="tutorial-highlight"
          onMouseDown={handleClick}
          style={{
            top: targetRect.top - 12,
            left: targetRect.left - 12,
            width: targetRect.width + 24,
            height: targetRect.height + 24,
          }}
        />
      )}

      {targetRect && (
        <div className="tutorial-stage" />
      )}
    </div>
  );
};

export default TutorialOverlay;
